import { CircularProgress, Container } from '@mui/material';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useAuth } from '../hooks/useAuth';
import { useUsers } from '../hooks/useUsers';
import { useContents } from 'hooks/useContent';
import { usePendingUsers } from 'hooks/usePendingUsers';
import { useUser } from 'hooks/useUser';
import { Content, IUser } from 'types';

interface AppDataType {
  [key: string]: any;
  isAppDataLoading: () => boolean;
  users: IUser[];
  contents: Content[];
  pendingUsers: IUser[];
  getUserData: () => void;
  getContentData: () => void;
  getPendingusers: () => void;
  fetchContents: () => Promise<void>;
  fetchPendingUsers: () => Promise<void>;
  updateUserById: (
    userId: string,
    userData: Partial<IUser>
  ) => Promise<
    | { success: boolean; message: string; data: any }
    | { success: boolean; message: string; data?: undefined }
  >;
  setSelectedType: (type: string) => void;
  selectedType: string;
  loading: boolean;
  error: string | null;
  user: IUser | null;
  setUser: React.Dispatch<React.SetStateAction<IUser | null>>;
  fetchContentById: (
    contentId: string
  ) => Promise<{ data: Content | null; error: string | null }>;
  updateContentById: (
    contentId: string,
    contentData: Partial<Content>
  ) => Promise<{ success: boolean; message: string; data?: Content }>;
}

export const AppDataContext = createContext<AppDataType>({} as AppDataType);

export function useAppData() {
  return useContext(AppDataContext);
}

export default function AppDataProvider(props: PropsWithChildren<any>) {
  const auth = useAuth();
  let { authLoading } = auth;
  const { fetchUserById, updateUserById } = useUser();
  const { users, userloading, userError, fetchUsers } = useUsers();
  const { pendingUsers, loading, error, fetchPendingUsers } = usePendingUsers();
  const [user, setUser] = useState<IUser | null>(null);
  const {
    contents,
    contentLoading,
    contentError,
    fetchContents,
    setSelectedType,
    selectedType,
    fetchContentById,
    updateContentById,
    setContents,
  } = useContents();

  const isAppDataLoading = useCallback(() => {
    return authLoading || userloading || contentLoading || loading;
  }, [authLoading, userloading, contentLoading, loading]);

  const getUserData = useCallback(() => {
    if (!users.length && !userloading && !userError) {
      fetchUsers();
    }
  }, [users, userloading, userError, fetchUsers]);

  const getContentData = useCallback(() => {
    if (!contents.length && !contentLoading && !contentError) {
      fetchContents();
    }
  }, [contents, contentLoading, contentError, fetchContents]);

  const getPendingusers = useCallback(() => {
    if (!pendingUsers.length && !loading && !error) {
      fetchPendingUsers();
    }
  }, [pendingUsers, loading, error, fetchPendingUsers]);

  return (
    <AppDataContext.Provider
      value={{
        users,
        contents,
        pendingUsers,
        getUserData,
        fetchUserById,
        getContentData,
        getPendingusers,
        fetchContents,
        fetchContentById,
        fetchPendingUsers,
        isAppDataLoading,
        setSelectedType,
        updateUserById,
        selectedType,
        loading,
        error,
        user,
        setUser,
        updateContentById,
        setContents,
      }}>
      {isAppDataLoading() ? (
        <Container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh", // Full viewport height
          }}
        >
          <CircularProgress />
        </Container>
      ) : (
        props.children
      )}
    </AppDataContext.Provider>
  );
}
